import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest
} from 'utilities/service.utils';
import LocateService from './locate.service.context';
import createUrlBuilder from 'utilities/url.builder.utils';
import { AxiosRequestConfig } from 'axios';
import { EditAutoRenew, AddAttachmentParams } from 'types';

export const getEnquiries = async (query: any) => {
  const config = {
    params: {
      ...query
    }
  };
  const urlBuilder = createUrlBuilder(`/enquiries`).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const getEnquiry = async (params: any) => {
  const config = {
    params: {
      slim: true
    }
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/${params.enquiryId}`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const getEnquiryDetails = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `/enquiries/${params.enquiryId}`
  ).toString();

  return await getRequest(LocateService, urlBuilder);
};

export const updateEnquiryDetails = async (enquiryId: number, payload: any) => {
  const urlBuilder = createUrlBuilder(`/enquiries/${enquiryId}`).toString();

  return await putRequest(LocateService, urlBuilder, payload);
};

export const getReferralDetails = async (
  enquiryId: number,
  assetOwnerId: number
) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${enquiryId}/assetOwners/${assetOwnerId}`
  ).toString();

  return await getRequest(LocateService, urlBuilder);
};

export const downloadCombinedPDF = async (params: any) => {
  const config = {
    params: {
      full: false,
      all: true
    }
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/${params.enquiryId}/download/pdf`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadCombinedPDFCheckReady = async (
  params: any,
  response: any
) => {
  const config = {
    params: {
      uid: response.uid,
      key: response.key
    }
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/${params.enquiryId}/view-pdf/${response.slug}`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadCombinedZIP = async (params: any) => {
  const config = {
    params: {
      full: true,
      all: false
    }
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/${params.enquiryId}/initial/zip`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadCombinedZIPCheckReady = async (
  params: any,
  response: any
) => {
  const config: AxiosRequestConfig = {
    params: {
      full: true,
      all: true
    },
    responseType: 'blob'
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/${params.enquiryId}/download/zip/${response.slug}`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadPartialZip = async (params: any) => {
  const config: AxiosRequestConfig = {
    params: {
      full: true,
      all: true
    },
    responseType: 'blob'
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/${params.enquiryId}/assetowners/${params.aoId}/download/zip`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadCombinedPDFAnonymous = async (params: any) => {
  const config = {
    params: {
      full: 'full',
      pl: params.plToken,
      vh: params.vhToken
    }
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/external/${params.enquiryId}/download/pdf`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadCombinedPDFCheckReadyAnonymous = async (
  params: any,
  response: any
) => {
  const config = {
    params: {
      uid: response.uid,
      key: response.key,
      pl: params.plToken,
      vh: params.vhToken
    }
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/external/${params.enquiryId}/view-pdf/${response.slug}`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadCombinedZIPAnonymous = async (params: any) => {
  const config = {
    params: {
      full: true,
      pl: params.plToken,
      vh: params.vhToken
    }
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/external/${params.enquiryId}/download/zip`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadCombinedZIPCheckReadyAnonymous = async (
  params: any,
  response: any
) => {
  const config: AxiosRequestConfig = {
    params: {
      pl: params.plToken,
      vh: params.vhToken
    },
    responseType: 'blob'
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/external/${params.enquiryId}/download/zip/${response.slug}`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const getAnonymousReferralDetails = async (
  enquiryId: number,
  assetOwnerId: number,
  plToken?: string,
  vhToken?: string
) => {
  const config = {
    params: {
      pl: plToken,
      vh: vhToken
    }
  };
  const urlBuilder = createUrlBuilder(
    `enquiries/external/${enquiryId}/referrals/${assetOwnerId}`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const downloadPartialZipAnonymous = async (params: any) => {
  const config: AxiosRequestConfig = {
    params: {
      full: true,
      all: true,
      pl: params.plToken,
      vh: params.vhToken
    },
    responseType: 'blob'
  };
  const urlBuilder = createUrlBuilder(
    `/enquiries/external/${params.enquiryId}/referrals/${params.aoId}/download/zip`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const searchJobIdUserRef = async (params: any) => {
  const config = {
    params: {
      ...params
    }
  };
  const urlBuilder = createUrlBuilder(`/enquiries/suggestions`).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const getAnonymousEnquiry = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `/enquiries/external/${params.enquiryId}?${params.token}`
  ).toString();

  return await getRequest(LocateService, urlBuilder);
};

export const getAnonymousEnquiryAutoRenewProperties = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `/enquiries/external/${params.enquiryId}/auto-renew-properties?${params.token}`
  ).toString();

  return await getRequest(LocateService, urlBuilder);
};

export const viewAttachment = async (params: any) => {
  const urlBuilder = createUrlBuilder(`viewAttachment/${params.id}`).toString();

  return await getRequest(LocateService, urlBuilder);
};

export const viewAttachmentAnonymous = async (params: any) => {
  const config = {
    params: {
      full: 'full',
      pl: params.plToken,
      vh: params.vhToken
    }
  };
  const urlBuilder = createUrlBuilder(
    `/viewAttachment/external/${params.id}`
  ).toString();

  return await getRequest(LocateService, urlBuilder, config);
};

export const getEnquiryAutoRenewProperties = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${params.enquiryId}/auto-renew-properties`
  ).toString();
  return await getRequest(LocateService, urlBuilder);
};

export const getEnquiryAutoRenewSeries = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${params.enquiryId}/auto-renew-series`
  ).toString();
  return await getRequest(LocateService, urlBuilder);
};

export const editEnquiryAutoRenew = async (params: EditAutoRenew) => {
  const data = {
    isEdit: params.isEdit,
    isDuplicate: params.isDuplicate,
    editId: params.editId,
    autoRenew: params.autoRenew,
    autoRenewEvery: params.autoRenewEvery,
    autoRenewUntil: params.autoRenewUntil,
    completionDate: params.completionDate
  };

  const urlBuilder = createUrlBuilder(
    `enquiries/${params.enquiryId}/edit`
  ).toString();

  return await putRequest(LocateService, urlBuilder, data);
};

export const archiveEnquiry = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${params.enquiryId}/archive`
  ).toString();
  const data = params.enquiryIdArray;

  return await putRequest(LocateService, urlBuilder, { enquiryIds: data });
};

export const unArchiveEnquiry = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${params.enquiryId}/unarchive`
  ).toString();
  const data = params.enquiryIdArray;

  return await putRequest(LocateService, urlBuilder, { enquiryIds: data });
};

export const moveEnquiryTeam = async (params: any) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${params.enquiryId}/moveTeam`
  ).toString();

  return await putRequest(LocateService, urlBuilder, params);
};

export const createAttachmentPresignedUrl = async (
  params: AddAttachmentParams
) => {
  const { file, presentationName, enquiryId, assetOwnerId } = params;
  const payload = {
    filename: file.name,
    presentation_name: presentationName,
    content_type: file.type,
    size: file.size
  };
  const urlBuilder = createUrlBuilder(
    `enquiries/${enquiryId}${
      assetOwnerId ? `/assetOwners/${assetOwnerId}` : ''
    }/attachments`
  ).toString();

  return await postRequest(LocateService, urlBuilder, payload);
};

export const deleteAttachment = async (
  id: number,
  enquiryId: number,
  assetOwnerId?: boolean
) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${enquiryId}${
      assetOwnerId ? `/assetOwners/${assetOwnerId}` : ''
    }/attachments/${id}`
  ).toString();

  return await deleteRequest(LocateService, urlBuilder);
};

export const updateAttachment = async (params: {
  attachmentId: number;
  enquiryId: number;
  is_hidden: boolean;
}) => {
  const urlBuilder = createUrlBuilder(
    `enquiries/${params.enquiryId}/attachments/${params.attachmentId}`
  ).toString();
  return await putRequest(LocateService, urlBuilder, params);
};

export const uploadEnquiriesShape = async (fileName: string) => {
  const urlBuilder = createUrlBuilder(`enquiries/shapes/upload`).toString();

  const body = {
    fileName
  };

  return await postRequest(LocateService, urlBuilder, body);
};

export const parseEnquiriesShape = async (s3Key: string) => {
  const urlBuilder = createUrlBuilder(`enquiries/shapes/parse`).toString();

  const body = {
    s3Key
  };

  return await postRequest(LocateService, urlBuilder, body);
};

export const bulkCreateEnquiry = async (data: any) => {
  const urlBuilder = createUrlBuilder(`enquiries/bulk/create`).toString();

  return await postRequest(LocateService, urlBuilder, data);
};

export const getEnquiryDraft = async () => {
  const urlBuilder = createUrlBuilder(`enquiries/draft`).toString();

  return await getRequest(LocateService, urlBuilder);
};

export const saveEnquiryDraft = async (data: any) => {
  const urlBuilder = createUrlBuilder(`enquiries/draft`).toString();

  return await postRequest(LocateService, urlBuilder, data);
};

export const discardEnquiryDraft = async () => {
  const urlBuilder = createUrlBuilder(`enquiries/draft`).toString();

  return await deleteRequest(LocateService, urlBuilder);
};
